import * as React from 'react';

import {
  enumCurrencyCode,
  enumDeductionApplicationType,
  enumUnit,
  Unit,
} from '@rematter/frontend-lib-api-client';
import {
  cn,
  MaterialSymbol,
  Row,
  TouchButton,
  TouchNumberField,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideMinusCircle, LucideTrash2 } from 'lucide-react';

import {
  $deleteEntry,
  $itemsInterfaceDisabled,
  $itemsInterfaceFacilityDefaultUnit,
  $itemsInterfaceInventoryLocked,
  $itemsInterfacePricingLocked,
  $itemsInterfaceShowPricing,
  $itemsInterfaceWeightUnitMode,
  $updateDeduction,
} from '../_state';
import { NormalizedDeduction, NormalizedLineItem } from '../_types';
import { PercentageField } from '../fields';
import { getItemRowGridCols, ITEM_ROW_ERROR_BORDER } from '../layout';
import { ItemRow } from '../layout/ItemRow';
import {
  calculateDeductionPercentage,
  calculateSiblingPercentageTotal,
  calculateWeightFromPercentage,
  clampPercentage,
} from '../utils/deductionPercentage';

import { useWeightConversion } from '~/modules/trade/hooks/useWeightConversion';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { enumLoadWeightType } from '~/modules/trade/types/load';
import { toMoney } from '~/modules/trade/utils/money';
import { toWeight } from '~/modules/trade/utils/weight';
import {
  SelectableDeductionType,
  SelectDeductionTypeTouch,
} from '~/shared/components/select-fields/SelectDeductionTypeTouch';
import { useUIPreferences } from '~/shared/hooks';

type ItemRowDeductionProps = {
  deduction: NormalizedDeduction;
  autoFocus?: boolean;
  onFocusRow?: (id: string) => void;
  hasError?: boolean;
  variant?: 'default' | 'secondary';
  parentLineItem?: NormalizedLineItem;
  siblingDeductions?: NormalizedDeduction[];
};

//
// ⚛��� ⚛️ ⚛�� ITEM ROW DEDUCTION ⚛️ ���️ ⚛️
//
export const ItemRowDeduction: React.FC<ItemRowDeductionProps> = ({
  deduction,
  autoFocus = false,
  onFocusRow,
  hasError,
  variant = 'default',
  parentLineItem,
  siblingDeductions,
}) => {
  // Translations
  const t = useTranslations();
  const labels = t.scaleTicketPro.itemsInterface;

  // Refs
  const weightFieldRef = React.useRef<HTMLDivElement>(null);
  const percentageRef = React.useRef<HTMLDivElement>(null);

  // Atoms
  const disabled = useAtomValue($itemsInterfaceDisabled);
  const inventoryLocked = useAtomValue($itemsInterfaceInventoryLocked);
  const pricingLocked = useAtomValue($itemsInterfacePricingLocked);
  const showPricing = useAtomValue($itemsInterfaceShowPricing);
  const weightUnit = useAtomValue($itemsInterfaceWeightUnitMode);
  const facilityDefaultUnit = useAtomValue($itemsInterfaceFacilityDefaultUnit);
  const onUpdateDeduction = useSetAtom($updateDeduction);
  const onDeleteDeduction = useSetAtom($deleteEntry);

  // Data
  const { touchModeEnabled } = useUIPreferences();
  const { convertToDisplayUnit, convertFromDisplayUnit, massUnitAbbreviation } =
    useWeightConversion({
      weightUnit,
      facilityDefaultUnit,
      pricingUnit: (deduction.pricingUnit as Unit) ?? enumUnit.Pounds,
    });

  // Memoized values
  const storedUnit = (deduction.weightValue?.unit as Unit) ?? enumUnit.Pounds;
  const deductionWeight = React.useMemo(
    () =>
      deduction.weightValue
        ? toWeight(deduction.weightValue.value, storedUnit)
        : undefined,
    [deduction.weightValue, storedUnit],
  );

  const weightDisplayValue = React.useMemo(() => {
    if (!deduction.weightValue) return 0;
    return convertToDisplayUnit(deductionWeight);
  }, [deduction.weightValue, convertToDisplayUnit, deductionWeight]);

  const isWeightBased = deduction.applicationType === 'Weight';
  const isSecondary = variant === 'secondary';
  const showPercentage = isWeightBased && isSecondary && !!parentLineItem;
  const gridClassName = getItemRowGridCols(showPricing);
  const isFieldDisabled = disabled || inventoryLocked || pricingLocked;

  const parentNetWeight = parentLineItem?.weights?.[enumLoadWeightType.Net];

  const percentage = React.useMemo(() => {
    if (deduction.percentage != null) return deduction.percentage;
    if (!showPercentage) return 0;
    return calculateDeductionPercentage(
      deduction.weightValue?.value ?? 0,
      parentNetWeight?.value ?? 0,
    );
  }, [
    deduction.percentage,
    deduction.weightValue?.value,
    showPercentage,
    parentNetWeight?.value,
  ]);

  const siblingPercentageTotal = React.useMemo(() => {
    if (!siblingDeductions) return 0;
    return calculateSiblingPercentageTotal(
      siblingDeductions,
      deduction.id,
      parentNetWeight?.value ?? 0,
    );
  }, [siblingDeductions, deduction.id, parentNetWeight?.value]);

  // Callbacks
  const handleSelectOption = React.useCallback(
    (option?: string, entity?: SelectableDeductionType) => {
      if (option && entity) {
        onUpdateDeduction(deduction.id, {
          deductionTypeId: option,
          deductionTypeName: entity.name,
          applicationType:
            entity.applicationType === enumDeductionApplicationType.Weight
              ? 'Weight'
              : 'Price',
        });
      }
    },
    [deduction.id, onUpdateDeduction],
  );

  const handleWeightChange = React.useCallback(
    (value: number) => {
      const convertedValue = convertFromDisplayUnit(value, storedUnit);
      const updates: Partial<NormalizedDeduction> = {
        weightValue: toWeight(convertedValue, storedUnit),
      };

      if (showPercentage) {
        const parentNet = parentNetWeight?.value ?? 0;
        const newPercentage = calculateDeductionPercentage(
          convertedValue,
          parentNet,
        );
        updates.percentage = clampPercentage(
          newPercentage,
          siblingPercentageTotal,
        );
      }

      onUpdateDeduction(deduction.id, updates);
    },
    [
      deduction.id,
      storedUnit,
      convertFromDisplayUnit,
      onUpdateDeduction,
      showPercentage,
      parentNetWeight?.value,
      siblingPercentageTotal,
    ],
  );

  const handlePercentageChange = React.useCallback(
    (newPercentage: number) => {
      const clamped = clampPercentage(newPercentage, siblingPercentageTotal);
      const parentNet = parentNetWeight?.value ?? 0;
      const unit =
        parentNetWeight?.unit ?? deduction.pricingUnit ?? enumUnit.Pounds;

      onUpdateDeduction(deduction.id, {
        percentage: clamped,
        weightValue: toWeight(
          calculateWeightFromPercentage(clamped, parentNet),
          unit,
        ),
      });
    },
    [
      deduction.id,
      deduction.pricingUnit,
      parentNetWeight,
      siblingPercentageTotal,
      onUpdateDeduction,
    ],
  );

  const handlePriceChange = React.useCallback(
    (value: number) => {
      const currencyCode =
        deduction.priceValue?.currencyCode ?? enumCurrencyCode.USD;

      onUpdateDeduction(deduction.id, {
        priceValue: toMoney({ amount: value || 0, currencyCode }),
      });
    },
    [deduction.id, deduction.priceValue?.currencyCode, onUpdateDeduction],
  );

  const handleDelete = React.useCallback(() => {
    onDeleteDeduction(deduction.id);
  }, [deduction.id, onDeleteDeduction]);

  // Effects
  React.useEffect(() => {
    if (!autoFocus) return;

    const targetRef = showPercentage ? percentageRef : weightFieldRef;
    const inputElement = targetRef?.current?.querySelector('input');
    if (inputElement) {
      setTimeout(() => {
        inputElement.focus();
        inputElement.select();
      }, 100);
    }
  }, [autoFocus, showPercentage]);

  return (
    <ItemRow
      gridTemplate={gridClassName}
      className={cn(
        '@ItemRowDeduction',
        'px-1 focus-within:rounded-lg focus-within:border-failure focus-within:ring-2 focus-within:ring-failure/10',
        hasError && ITEM_ROW_ERROR_BORDER,
        isSecondary && 'relative after:left-[40px]',
      )}
      onFocus={() => onFocusRow?.(deduction.id)}
    >
      <div
        className={cn(
          '@DeductionDescription',
          isSecondary && 'col-span-2 pl-[40px]',
        )}
      >
        <Row xCenter gap2 className="h-full">
          <MaterialSymbol
            color="failure"
            className="w-[35px] max-w-[35px]"
            symbolIcon={<LucideMinusCircle size={16} />}
          />

          <SelectDeductionTypeTouch
            className="h-full min-h-full flex-1"
            color="failure"
            placeholder={labels.emptyDescription}
            value={deduction.deductionTypeId}
            onChange={handleSelectOption}
            isDisabled={isFieldDisabled}
          />
        </Row>
      </div>

      {showPercentage ? (
        <div ref={percentageRef}>
          <PercentageField
            color="failure"
            lineItem={parentLineItem}
            percentage={percentage}
            onPercentageChange={handlePercentageChange}
            disabled={isFieldDisabled}
          />
        </div>
      ) : (
        <div />
      )}
      {!isSecondary && <div />}

      <div ref={weightFieldRef}>
        {isWeightBased && (
          <TouchNumberField
            aria-label={labels.deductionWeight}
            color="failure"
            value={weightDisplayValue}
            onChange={handleWeightChange}
            formatOptions={{
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            }}
            touchModeEnabled={touchModeEnabled}
            suffix={massUnitAbbreviation}
            isDisabled={isFieldDisabled}
            className="@DeductionWeightField"
          />
        )}
      </div>

      <>
        <div />
        {!isWeightBased ? (
          <>
            <TouchNumberField
              aria-label={labels.deductionPrice}
              color="failure"
              value={deduction.priceValue?.amount}
              onChange={handlePriceChange}
              prefix="$"
              formatOptions={{
                minimumFractionDigits: 2,
                maximumFractionDigits: 8,
              }}
              touchModeEnabled={touchModeEnabled}
              isDisabled={isFieldDisabled}
              className="@DeductionPriceField"
            />
            <TouchNumberField
              aria-label={labels.estimatedDeductionPrice}
              color="failure"
              isDisabled
              value={(deduction.priceValue?.amount ?? 0) * weightDisplayValue}
              prefix="$"
              formatOptions={{
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }}
            />
          </>
        ) : (
          <>
            <div />
            {deduction.calculatedAmount ? (
              <TouchNumberField
                aria-label={labels.estimatedDeductionPrice}
                color="failure"
                isDisabled
                value={deduction.calculatedAmount.amount}
                prefix="$"
                formatOptions={{
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }}
              />
            ) : (
              <div />
            )}
          </>
        )}
      </>

      <div />
      <div />
      <TouchButton
        variant="ghost"
        color="failure"
        onPress={handleDelete}
        isDisabled={isFieldDisabled}
        aria-label={labels.deleteDeduction}
        className={cn('@DeleteButtonField', 'max-h-[44px]')}
        isSelected={false}
      >
        <LucideTrash2 size={16} />
      </TouchButton>
    </ItemRow>
  );
};
